import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { photoService } from '../../services/api';
import PhotoUpload from '../../components/PhotoUpload/PhotoUpload';
import PhotoLightbox from '../../components/PhotoLightbox/PhotoLightbox';
import { useAuth } from '../../context/AuthContext';
import { FiArrowLeft, FiArrowRight, FiPlus, FiEdit2, FiTrash2, FiChevronUp, FiChevronDown, FiImage, FiX } from 'react-icons/fi';
import './AlbumView.css';

const AlbumView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const fromEventAlbums = location.state?.from === 'event-albums';
  const fromFamilyAlbums = location.state?.from === 'family-albums';
  const fromProfile = location.state?.from === 'profile';
  const fromMemberID = location.state?.memberID;
  const { user } = useAuth();
  const [album, setAlbum] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [sortAsc, setSortAsc] = useState(false);
  const [showAddExisting, setShowAddExisting] = useState(false);
  const [availablePhotos, setAvailablePhotos] = useState([]);
  const [selectedPhotoIds, setSelectedPhotoIds] = useState([]);
  const [loadingAvailable, setLoadingAvailable] = useState(false);
  const [addingPhotos, setAddingPhotos] = useState(false);

  const fetchAlbum = useCallback(async () => {
    try {
      setLoading(true);
      const response = await photoService.getAlbumWithPhotos(id);
      if (response.success) {
        setAlbum(response.data.album || response.data);
        const photoData = response.data.photos || response.data.items || [];
        setPhotos(Array.isArray(photoData) ? photoData : []);
      } else {
        setError('Album not found');
      }
    } catch (err) {
      console.error('Failed to fetch album:', err);
      setError('Failed to load album');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const handleDeleteAlbum = async () => {
    if (!window.confirm('Delete this album? Photos will be kept in your unassigned photos.')) return;
    try {
      const response = await photoService.deleteAlbum(parseInt(id));
      if (response.success) {
        navigate('/photos');
      }
    } catch (err) {
      console.error('Failed to delete album:', err);
    }
  };

  useEffect(() => {
    fetchAlbum();
  }, [fetchAlbum]);

  const handlePhotoUploaded = (newPhoto) => {
    setPhotos(prev => [newPhoto, ...prev]);
    setShowUpload(false);
  };

  const openLightbox = (photo, index) => {
    setLightboxPhoto(photo);
    setLightboxIndex(index);
  };

  const closeLightbox = () => {
    setLightboxPhoto(null);
  };

  const navigateLightbox = (direction) => {
    const newIndex = lightboxIndex + direction;
    if (newIndex >= 0 && newIndex < sortedPhotos.length) {
      setLightboxIndex(newIndex);
      setLightboxPhoto(sortedPhotos[newIndex]);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    try {
      const response = await photoService.deletePhoto(photoId);
      if (response.success) {
        setPhotos(prev => prev.filter(p => p.photoID !== photoId));
        closeLightbox();
      }
    } catch (err) {
      console.error('Failed to delete photo:', err);
    }
  };

  const fetchAvailablePhotos = async () => {
    try {
      setLoadingAvailable(true);
      const response = await photoService.getMyPhotos();
      if (response.success) {
        const allPhotos = Array.isArray(response.data)
          ? response.data
          : (response.data.photos || response.data.items || []);
        // Filter out photos already in this album
        const available = allPhotos.filter(p => p.albumID !== parseInt(id));
        setAvailablePhotos(available);
      }
    } catch (err) {
      console.error('Failed to fetch photos:', err);
    } finally {
      setLoadingAvailable(false);
    }
  };

  const togglePhotoSelection = (photoId) => {
    setSelectedPhotoIds(prev =>
      prev.includes(photoId)
        ? prev.filter(id => id !== photoId)
        : [...prev, photoId]
    );
  };

  const handleAddExistingPhotos = async () => {
    try {
      setAddingPhotos(true);
      for (const photoId of selectedPhotoIds) {
        await photoService.updatePhoto(photoId, { albumID: parseInt(id) });
      }
      setShowAddExisting(false);
      setSelectedPhotoIds([]);
      fetchAlbum();
    } catch (err) {
      console.error('Failed to add photos:', err);
    } finally {
      setAddingPhotos(false);
    }
  };

  const sortedPhotos = [...photos].sort((a, b) => {
    const dateA = new Date(a.takenDate || a.createdDate);
    const dateB = new Date(b.takenDate || b.createdDate);
    return sortAsc ? dateA - dateB : dateB - dateA;
  });

  const groupedPhotos = album?.eventID
    ? Object.values(
      sortedPhotos.reduce((groups, photo) => {
        const key = photo.memberID;
        if (!groups[key]) groups[key] = { member: photo.member, photos: [] };
        groups[key].photos.push(photo);
        return groups;
      }, {})
    )
    : null;

  if (loading) {
    return (
      <div className="album-view-page">
        <div className="container">
          <div className="loading-state">
            <span className="spinner spinner-lg"></span>
            <p>Loading album...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !album) {
    return (
      <div className="album-view-page">
        <div className="container">
          <div className="error-state card">
            <p>{error || 'Album not found'}</p>
            <Link to="/photos" className="btn btn-primary">Back to Photos</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="album-view-page">
      <div className="container">
        {/* Header */}
        <div className="album-view-header">
          <button className="back-btn" onClick={() => {
            if (fromEventAlbums || fromFamilyAlbums) navigate(-1);
            else if (fromProfile) navigate(`/member/${fromMemberID}`, { state: { activeTab: 'photos' } });
            else navigate('/photos');
          }}>
            <FiArrowLeft /> {fromEventAlbums ? 'Back to Event Albums' : fromFamilyAlbums ? 'Back to Family Albums' : fromProfile ? 'Back to Profile' : 'Back to Photos'}
          </button>

          <div className="album-view-info">
            <h1>{album.albumName}</h1>
            {album.description && (
              <p className="album-description">{album.description}</p>
            )}
            <p className="album-meta">{photos.length} photos</p>
          </div>

          <div className="album-view-actions">
            <button className="btn btn-secondary btn-sm" onClick={() => setSortAsc(!sortAsc)}>
              {sortAsc ? <><FiChevronUp /> Oldest First</> : <><FiChevronDown /> Newest First</>}
            </button>
            <button className="btn btn-secondary btn-sm" onClick={() => { setShowAddExisting(true); fetchAvailablePhotos(); }}>
              <FiImage /> Add Existing
            </button>
            <button className="btn btn-primary" onClick={() => setShowUpload(true)}>
              <FiPlus /> Upload New
            </button>
            {!album.eventID && (album.memberID === user?.memberID || user?.isAdmin) && (
              <button className="btn btn-danger btn-sm" onClick={handleDeleteAlbum}>
                <FiTrash2 /> Delete Album
              </button>
            )}
          </div>
        </div>

        {/* Photos Grid */}
        {photos.length === 0 ? (
          <div className="empty-state card">
            <div className="empty-state-icon">📷</div>
            <h3 className="empty-state-title">No photos in this album</h3>
            <p>Add some photos to get started!</p>
            <button className="btn btn-primary" onClick={() => setShowUpload(true)}>
              <FiPlus /> Add Photos
            </button>
          </div>
        ) : groupedPhotos ? (
          <div className="event-album-groups">
            {groupedPhotos.map((group, groupIndex) => (
              <div key={group.member?.memberID} className="event-album-group">
                <div className="event-album-group-header">
                  <span className="event-album-group-name">
                    {group.member?.fullName || 'Unknown'}
                  </span>
                </div>
                <div className="photos-grid">
                  {group.photos.map((photo) => {
                    const index = sortedPhotos.findIndex(p => p.photoID === photo.photoID);
                    return (
                      <div
                        key={photo.photoID}
                        className="photo-item"
                        onClick={() => openLightbox(photo, index)}
                      >
                        {photo.fileType === 'video' ? (
                          <>
                            {photo.thumbnailURL ? (
                              <img src={photo.thumbnailURL} alt={photo.caption || 'Video'} />
                            ) : (
                              <div className="video-tile-placeholder" />
                            )}
                            <div className="video-play-overlay">▶</div>
                          </>
                        ) : (
                          <img
                            src={photo.thumbnailURL || photo.fileURL}
                            alt={photo.caption || 'Photo'}
                          />
                        )}
                        {photo.caption && (
                          <div className="photo-caption">{photo.caption}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {groupIndex < groupedPhotos.length - 1 && <hr className="event-album-divider" />}
              </div>
            ))}
          </div>
        ) : (
          <div className="photos-grid">
            {sortedPhotos.map((photo, index) => (
              <div
                key={photo.photoID}
                className="photo-item"
                onClick={() => openLightbox(photo, index)}
              >
                {photo.fileType === 'video' ? (
                  <>
                    {photo.thumbnailURL ? (
                      <img src={photo.thumbnailURL} alt={photo.caption || 'Video'} />
                    ) : (
                      <div className="video-tile-placeholder" />
                    )}
                    <div className="video-play-overlay">▶</div>
                  </>
                ) : (
                  <img
                    src={photo.thumbnailURL || photo.fileURL}
                    alt={photo.caption || 'Photo'}
                  />
                )}
                {photo.caption && (
                  <div className="photo-caption">{photo.caption}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {showUpload && (
        <PhotoUpload
          onClose={() => setShowUpload(false)}
          onUploaded={handlePhotoUploaded}
          albums={[album]}
          defaultAlbum={album.albumID}
        />
      )}

      {/* Lightbox */}
      {lightboxPhoto && (
        <PhotoLightbox
          photo={lightboxPhoto}
          onClose={closeLightbox}
          onPrev={() => navigateLightbox(-1)}
          onNext={() => navigateLightbox(1)}
          hasPrev={lightboxIndex > 0}
          hasNext={lightboxIndex < sortedPhotos.length - 1}
          onDelete={lightboxPhoto.memberID === user?.memberID || user?.isAdmin ? handleDeletePhoto : null}
          onCaptionUpdated={(photoId, newCaption) => {
            setPhotos(prev => prev.map(p => p.photoID === photoId ? { ...p, caption: newCaption } : p));
            setLightboxPhoto(prev => ({ ...prev, caption: newCaption }));
          }}
        />
      )}

      {/* Add Existing Photos Modal */}
      {showAddExisting && (
        <div className="modal-overlay" onClick={() => setShowAddExisting(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px', maxHeight: '80vh' }}>
            <div className="modal-header">
              <h3>Add Photos to Album</h3>
              <button className="btn btn-ghost" onClick={() => setShowAddExisting(false)}><FiX /></button>
            </div>
            <div className="modal-body" style={{ overflowY: 'auto' }}>
              {loadingAvailable ? (
                <div className="loading-state">
                  <span className="spinner"></span>
                  <p>Loading photos...</p>
                </div>
              ) : availablePhotos.length === 0 ? (
                <div className="empty-state">
                  <p>No photos available to add. Upload new photos first.</p>
                </div>
              ) : (
                <>
                  <p style={{ marginBottom: '12px', fontSize: '0.9rem', color: '#666' }}>
                    Select photos to add ({selectedPhotoIds.length} selected)
                  </p>
                  <div className="add-existing-grid">
                    {availablePhotos.map(photo => (
                      <div
                        key={photo.photoID}
                        className={`add-existing-item ${selectedPhotoIds.includes(photo.photoID) ? 'selected' : ''}`}
                        onClick={() => togglePhotoSelection(photo.photoID)}
                      >
                        <img src={photo.thumbnailURL || photo.fileURL} alt={photo.caption || 'Photo'} />
                        {selectedPhotoIds.includes(photo.photoID) && (
                          <div className="add-existing-check">✓</div>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setShowAddExisting(false)}>Cancel</button>
              <button
                className="btn btn-primary"
                onClick={handleAddExistingPhotos}
                disabled={selectedPhotoIds.length === 0 || addingPhotos}
              >
                {addingPhotos ? 'Adding...' : `Add ${selectedPhotoIds.length} Photo${selectedPhotoIds.length !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AlbumView;
